/*
 * This file is part of the https://gitlab-intern.easd-support.de/easd/p26s26_energie_watchdog project.
 *
 * (c) 2026 EA Systems Dresden GmbH
 *
 * For the full copyright and license information, please view the LICENSE.md
 * file that was distributed with this source code.
 */

import { TriangleAlert } from 'lucide-react';

/* Import configuration */
import type { TimeSeriesGroupKey } from '@/config/eeCheckConfig';

/* Import components */
import DemoSensorDeviceFilterControls from '@/components/tabs/demo/DemoSensorDeviceFilterControls';

/* Import i18n functions */
import { useI18n } from '@/context/I18nContext';

/* Import libraries */
import { DeviceSensor } from '@/lib/sensors/catalog/deviceSensor';
import type { DeviceSensorCatalog } from '@/lib/sensors/catalog/deviceSensorCatalog';
import type { SensorMetadataEntry } from '@/lib/sensors/csv/csvToSensorMetadata';
import type { DemoSensorTarget, SensorGroupFilter } from '@/lib/sensors/demo/demoDeviceSensors';
import type {
    DemoSensorDeviceAvailability,
    DemoSensorDeviceGroupBy,
} from '@/lib/sensors/demo/demoSensorDeviceSelection';

interface DemoSensorDeviceSelectionProps {
    availability: DemoSensorDeviceAvailability;
    customer: TimeSeriesGroupKey;
    deviceMetadata: SensorMetadataEntry | null;
    deviceSensor: DeviceSensor;
    deviceSensorCatalog: DeviceSensorCatalog;
    groupBy: DemoSensorDeviceGroupBy;
    groupFilter: SensorGroupFilter;
    onAvailabilityChange: (availability: DemoSensorDeviceAvailability) => void;
    onCustomerChange: (customer: TimeSeriesGroupKey) => void;
    onDeviceChange: (device: string) => void;
    onGroupByChange: (groupBy: DemoSensorDeviceGroupBy) => void;
    onGroupFilterChange: (groupFilter: SensorGroupFilter) => void;
    onSensorTargetChange: (target: string) => void;
    selectedDevice: string;
    selectedSensorTarget: string;
    showSensorTypeSelect?: boolean;
    sensorTargets: DemoSensorTarget[];
    virtualDevices: string[];
}

function DemoSensorDeviceSelection({
    availability,
    customer,
    deviceMetadata,
    deviceSensor,
    deviceSensorCatalog,
    groupBy,
    groupFilter,
    onAvailabilityChange,
    onCustomerChange,
    onDeviceChange,
    onGroupByChange,
    onGroupFilterChange,
    onSensorTargetChange,
    selectedDevice,
    selectedSensorTarget,
    showSensorTypeSelect = true,
    sensorTargets,
    virtualDevices,
}: DemoSensorDeviceSelectionProps) {
    const { t } = useI18n();
    return (
        <>
            <DemoSensorDeviceFilterControls
                availability={availability}
                customer={customer}
                deviceSensorCatalog={deviceSensorCatalog}
                groupBy={groupBy}
                groupFilter={groupFilter}
                onAvailabilityChange={onAvailabilityChange}
                onCustomerChange={onCustomerChange}
                onDeviceChange={onDeviceChange}
                onGroupByChange={onGroupByChange}
                onGroupFilterChange={onGroupFilterChange}
                selectedDevice={selectedDevice}
                virtualDevices={virtualDevices}
            />

            {selectedDevice ? (
                <>
                    <section className="dashboard-panel flex items-stretch justify-start p-5">
                        <dl className="grid w-full grid-cols-1 gap-3 sm:grid-cols-5">
                            <div className="min-w-0 rounded-box bg-base-200/60 px-3 py-2">
                                <dt className="text-xs font-semibold uppercase tracking-[0.08em] text-base-content/60">{t('Name')}</dt>
                                <dd className="mt-1 break-words text-sm text-base-content">{deviceMetadata?.name ?? 'N/A'}</dd>
                            </div>
                            <div className="min-w-0 rounded-box bg-base-200/60 px-3 py-2">
                                <dt className="text-xs font-semibold uppercase tracking-[0.08em] text-base-content/60">{t('Address')}</dt>
                                <dd className="mt-1 break-words text-sm text-base-content">{deviceMetadata?.address ?? 'N/A'}</dd>
                            </div>
                            <div className="min-w-0 rounded-box bg-base-200/60 px-3 py-2">
                                <dt className="text-xs font-semibold uppercase tracking-[0.08em] text-base-content/60">{t('Description')}</dt>
                                <dd className="mt-1 break-words text-sm text-base-content">{deviceMetadata?.description ?? 'N/A'}</dd>
                            </div>
                            <div className="min-w-0 rounded-box bg-base-200/60 px-3 py-2">
                                <dt className="text-xs font-semibold uppercase tracking-[0.08em] text-base-content/60">{t('Category')}</dt>
                                <dd className="mt-1 break-words text-sm text-base-content">{deviceSensor.getDeviceCategoryLabel() ?? 'N/A'}</dd>
                            </div>
                            <div className="min-w-0 rounded-box bg-base-200/60 px-3 py-2">
                                <dt className="text-xs font-semibold uppercase tracking-[0.08em] text-base-content/60">{t('Enabled')}</dt>
                                <dd className="mt-1 break-words text-sm text-base-content">
                                    {deviceMetadata ? deviceMetadata.enabled ? t('Sensor enabled') : t('Sensor not enabled') : 'N/A'}
                                </dd>
                            </div>
                        </dl>
                    </section>

                    {deviceMetadata && !deviceMetadata.enabled ? (
                        <section className="alert alert-warning" role="alert">
                            <TriangleAlert className="h-5 w-5" aria-hidden="true" />
                            <span>{t('Attention: This is a disabled sensor. The request may not return any time series data.')}</span>
                        </section>
                    ) : null}

                    {showSensorTypeSelect ? (
                        <section className="dashboard-panel flex items-stretch justify-start p-5">
                            <div className="flex w-full flex-col gap-2 text-left">
                                <label className="text-sm font-semibold" htmlFor="demo-sensor-type-select">
                                    {t('Sensor type')}
                                </label>
                                <select
                                    id="demo-sensor-type-select"
                                    className="select select-bordered w-full"
                                    value={selectedSensorTarget}
                                    onChange={(event) => onSensorTargetChange(event.target.value)}
                                >
                                    {sensorTargets.map((target) => (
                                        <option key={`${target.group}-${target.sensorType}-${target.sensorName}`} value={target.sensorName ?? ''}>
                                            {`${target.sensorType} -> ${target.sensorName}`}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </section>
                    ) : null}
                </>
            ) : (
                <div className="rounded-box border border-dashed border-base-300 px-4 py-8 text-center text-sm text-base-content/70">
                    {t('No sensor/device is available for the current selection.')}
                </div>
            )}
        </>
    );
}

export default DemoSensorDeviceSelection;
